import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import { Icons } from '../components/Icons';

function CustomerManagement() {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: '고객',
    contact: '',
    email: '',
    address: '',
    notes: ''
  });
  const [searchTerm, setSearchTerm] = useState('');

  // 거래처 불러오기
  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const customersRef = collection(db, 'customers');
      const snapshot = await getDocs(customersRef);

      const customerList = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));

      setCustomers(customerList);
    } catch (error) {
      console.error('거래처 불러오기 오류:', error);
      alert('거래처를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 거래처 추가
  const handleAddCustomer = async () => {
    if (!formData.name.trim()) {
      alert('거래처 이름을 입력하세요.');
      return;
    }

    try {
      await addDoc(collection(db, 'customers'), {
        ...formData,
        createdAt: serverTimestamp(),
        createdBy: user.email
      });

      alert('✅ 거래처가 추가되었습니다.');
      setShowAddModal(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      console.error('거래처 추가 오류:', error);
      alert('거래처 추가 중 오류가 발생했습니다.');
    }
  };

  // 거래처 수정
  const handleUpdateCustomer = async () => {
    if (!formData.name.trim()) {
      alert('거래처 이름을 입력하세요.');
      return;
    }

    try {
      const customerRef = doc(db, 'customers', editingCustomer.id);
      await updateDoc(customerRef, {
        ...formData,
        updatedAt: serverTimestamp(),
        updatedBy: user.email
      });

      alert('✅ 거래처가 수정되었습니다.');
      setEditingCustomer(null);
      resetForm();
      fetchCustomers();
    } catch (error) {
      console.error('거래처 수정 오류:', error);
      alert('거래처 수정 중 오류가 발생했습니다.');
    }
  };

  // 거래처 삭제
  const handleDeleteCustomer = async (customerId, customerName) => {
    if (!window.confirm(`"${customerName}" 거래처를 삭제하시겠습니까?`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'customers', customerId));
      alert('✅ 거래처가 삭제되었습니다.');
      fetchCustomers();
    } catch (error) {
      console.error('거래처 삭제 오류:', error);
      alert('거래처 삭제 중 오류가 발생했습니다.');
    }
  };

  // 수정 시작
  const startEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      type: customer.type,
      contact: customer.contact || '',
      email: customer.email || '',
      address: customer.address || '',
      notes: customer.notes || ''
    });
  };

  // 폼 초기화
  const resetForm = () => {
    setFormData({
      name: '',
      type: '고객',
      contact: '',
      email: '',
      address: '',
      notes: ''
    });
  };

  // 모달 닫기
  const closeModal = () => {
    setShowAddModal(false);
    setEditingCustomer(null);
    resetForm();
  };

  // 검색 필터링
  const filteredCustomers = customers.filter(customer =>
    customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.contact?.includes(searchTerm) ||
    customer.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-3xl font-bold text-brand-900">거래처 관리</h2>
          <p className="text-gray-500 mt-1">고객, 판매, 대량 등 거래처 정보를 관리합니다.</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition font-bold flex items-center shadow-md"
        >
          <Icons.Plus className="w-5 h-5 mr-2" />
          거래처 추가
        </button>
      </div>

      {/* 검색 */}
      <div className="mb-6 bg-white rounded-xl shadow-sm border border-brand-100 p-4">
        <div className="relative">
          <Icons.Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="이름, 전화번호, 이메일, 주소, 메모 검색"
            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* 거래처 목록 */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">거래처를 불러오는 중...</p>
        </div>
      ) : filteredCustomers.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Icons.Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg mb-4">
            {searchTerm ? '검색 결과가 없습니다.' : '등록된 거래처가 없습니다.'}
          </p>
          {!searchTerm && (
            <button
              onClick={() => setShowAddModal(true)}
              className="px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition font-bold"
            >
              첫 거래처 추가하기
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-brand-50 border-b border-brand-100">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-bold text-brand-900">종류</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-brand-900">이름</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-brand-900">전화번호</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-brand-900">이메일</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-brand-900">주소</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-brand-900">메모</th>
                <th className="px-6 py-4 text-right text-sm font-bold text-brand-900">작업</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredCustomers.map((customer) => (
                <tr key={customer.id} className="hover:bg-gray-50 transition">
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                      customer.type === '고객' ? 'bg-blue-100 text-blue-700' :
                      customer.type === '판매' ? 'bg-green-100 text-green-700' :
                      'bg-purple-100 text-purple-700'
                    }`}>
                      {customer.type}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Icons.User className="w-5 h-5 text-gray-400 mr-2" />
                      <span className="font-bold text-gray-900">{customer.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-700">{customer.contact || '-'}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-700">{customer.email || '-'}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-700 text-sm">{customer.address || '-'}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-500 text-sm">{customer.notes || '-'}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => startEdit(customer)}
                        className="px-3 py-1.5 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-lg transition text-sm font-medium"
                      >
                        수정
                      </button>
                      <button
                        onClick={() => handleDeleteCustomer(customer.id, customer.name)}
                        className="px-3 py-1.5 bg-white hover:bg-red-50 border border-gray-300 hover:border-red-300 text-gray-700 hover:text-red-600 rounded-lg transition text-sm font-medium"
                      >
                        삭제
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 추가/수정 모달 */}
      {(showAddModal || editingCustomer) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">
                {editingCustomer ? '거래처 수정' : '거래처 추가'}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <Icons.X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* 거래처 종류 */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  종류 <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent bg-white"
                >
                  <option value="고객">고객</option>
                  <option value="판매">판매</option>
                  <option value="대량">대량</option>
                </select>
              </div>

              {/* 거래처 이름 */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  이름 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="거래처 이름을 입력하세요"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>

              {/* 전화번호 & 이메일 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    전화번호
                  </label>
                  <input
                    type="tel"
                    value={formData.contact}
                    onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                    placeholder="010-1234-5678"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    이메일
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="example@email.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* 주소 */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  주소
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="주소를 입력하세요"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>

              {/* 메모 */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  메모
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="3"
                  placeholder="메모를 입력하세요"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button
                onClick={closeModal}
                className="px-6 py-3 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-lg transition font-bold"
              >
                취소
              </button>
              <button
                onClick={editingCustomer ? handleUpdateCustomer : handleAddCustomer}
                className="px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition font-bold"
              >
                {editingCustomer ? '수정' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerManagement;
